/**
 * Hook: Invite/Update/Delete user actions with toast and refetch for Invite Users page.
 * Single Responsibility: call services, refetch, show toasts, handle primary-admin error.
 */

import { useCallback } from 'react';
import { inviteUser, updateUser, deleteUser } from '../../../services/userInviteService';
import {
  ERROR_MESSAGES,
  TOAST_CONFIG,
  getInviteSuccessCopy,
} from '../constants';

function pluralize(count, singular, plural = `${singular}s`) {
  return count !== 1 ? plural : singular;
}

export function useInviteUsersActions(showToast, refetchUsers, setSelectedUserIds) {
  const handleCreateUser = useCallback(
    async (data) => {
      try {
        const response = await inviteUser(data);
        if (response?.success && response?.data) {
          const newUser = response.data;
          await refetchUsers();
          showToast({
            variant: 'success',
            title: TOAST_CONFIG.TITLE_SUCCESS,
            copy: getInviteSuccessCopy(newUser.email),
          });
        } else {
          throw new Error(ERROR_MESSAGES.INVALID_RESPONSE);
        }
      } catch (error) {
        showToast({
          variant: 'error',
          title: TOAST_CONFIG.TITLE_ERROR,
          copy: error?.message || ERROR_MESSAGES.INVITE_USER,
        });
        throw error;
      }
    },
    [showToast, refetchUsers],
  );

  const handleUpdateUser = useCallback(
    async (userId, data) => {
      try {
        const response = await updateUser(userId, data);
        if (response?.success && response?.data) {
          await refetchUsers();
          showToast({
            variant: 'success',
            title: TOAST_CONFIG.TITLE_SUCCESS,
            copy: 'User updated successfully',
          });
        } else {
          throw new Error(ERROR_MESSAGES.INVALID_RESPONSE);
        }
      } catch (error) {
        showToast({
          variant: 'error',
          title: TOAST_CONFIG.TITLE_ERROR,
          copy: error?.message || ERROR_MESSAGES.UPDATE_USER,
        });
        throw error;
      }
    },
    [showToast, refetchUsers],
  );

  const handleDeleteUser = useCallback(
    async (userId, onSuccess) => {
      try {
        const response = await deleteUser(userId);
        if (response?.success && response?.data) {
          await refetchUsers();
          showToast({
            variant: 'success',
            title: TOAST_CONFIG.TITLE_SUCCESS,
            copy: 'User deleted successfully',
          });
          onSuccess?.();
        } else {
          throw new Error(ERROR_MESSAGES.INVALID_RESPONSE);
        }
      } catch (error) {
        if (error?.code === 'CANNOT_DELETE_PRIMARY_ADMIN') {
          showToast({
            variant: 'error',
            title: TOAST_CONFIG.TITLE_CANNOT_DELETE,
            copy: error.message || ERROR_MESSAGES.CANNOT_DELETE_PRIMARY_ADMIN,
          });
        } else {
          showToast({
            variant: 'error',
            title: TOAST_CONFIG.TITLE_ERROR,
            copy: error?.message || ERROR_MESSAGES.DELETE_USER,
          });
        }
        throw error;
      }
    },
    [showToast, refetchUsers],
  );

  const handleDeleteSelected = useCallback(
    async (selectedUserIdsSet) => {
      const count = selectedUserIdsSet.size;
      if (count === 0) return;
      // Caller is responsible for confirmation (e.g. DeleteUserModal in bulk mode)

      const ids = Array.from(selectedUserIdsSet);
      const deletePromises = ids.map((id) => deleteUser(id));
      const results = await Promise.allSettled(deletePromises);
      const failures = results.filter((r) => r.status === 'rejected');
      const successes = results.filter((r) => r.status === 'fulfilled');
      const primaryAdminErrors = failures.filter(
        (f) => f.reason?.code === 'CANNOT_DELETE_PRIMARY_ADMIN',
      );

      if (failures.length > 0) {
        if (primaryAdminErrors.length > 0) {
          showToast({
            variant: 'error',
            title: TOAST_CONFIG.TITLE_CANNOT_DELETE_SOME,
            copy: ERROR_MESSAGES.CANNOT_DELETE_SOME_USERS,
          });
        } else {
          showToast({
            variant: 'error',
            title: TOAST_CONFIG.TITLE_ERROR,
            copy: `Failed to delete ${failures.length} ${pluralize(failures.length, 'user')}`,
          });
        }
      }

      if (successes.length > 0) {
        await refetchUsers();
        setSelectedUserIds(new Set());
        showToast({
          variant: 'success',
          title: TOAST_CONFIG.TITLE_SUCCESS,
          copy: `${successes.length} ${pluralize(successes.length, 'user')} deleted successfully`,
        });
      }
    },
    [showToast, refetchUsers, setSelectedUserIds],
  );

  return {
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleDeleteSelected,
  };
}
